import type { ArbitragemSituacao } from '../types/arbitragemDetalheTypes';
import type { CitacaoArbitralDetail, CitacaoArbitralListItem } from '../types/portalArbitralTypes';

export type ChipProcessoTom = 'warning' | 'brand' | 'success' | 'neutral' | 'error';

export type ChipProcessoIcone = 'relogio' | 'check' | 'balanca';

export const TOM_CLASSES: Record<ChipProcessoTom, string> = {
  warning: 'border-warning-200 bg-warning-50 text-warning-700',
  brand: 'border-brand-200 bg-brand-50 text-brand-700',
  success: 'border-success-200 bg-success-50 text-success-700',
  neutral: 'border-neutral-200 bg-neutral-50 text-neutral-600',
  error: 'border-error-200 bg-error-50 text-error-600',
};

const SITUACAO_TOM: Partial<Record<ArbitragemSituacao, ChipProcessoTom>> = {
  encerrado: 'success',
  manifestou: 'brand',
  prazo_perdido: 'error',
  prazo_acabando: 'warning',
  prazo_aberto: 'warning',
  contato_pendente: 'neutral',
};

export const situacaoParaTom = (situacao: ArbitragemSituacao | undefined): ChipProcessoTom =>
  (situacao && SITUACAO_TOM[situacao]) || 'neutral';

export const isProcessoEncerrado = (
  processo: Pick<CitacaoArbitralListItem, 'processoEncerrado' | 'status'>,
): boolean => processo.processoEncerrado || processo.status === 'CONCLUIDO';

interface ChipProcesso {
  rotulo: string;
  tom: ChipProcessoTom;
  icone: ChipProcessoIcone;
}

const MS_POR_DIA = 24 * 60 * 60 * 1000;

export const diasAtePrazo = (prazoIso: string, referenciaIso?: string): number => {
  const prazo = new Date(prazoIso).getTime();
  const referencia = referenciaIso ? new Date(referenciaIso).getTime() : Date.now();
  return Math.ceil((prazo - referencia) / MS_POR_DIA);
};

/**
 * O card mostrava "Prazo encerrado"/"Resposta enviada" no chip e ainda assim renderizava o
 * botão Responder (só escondia com o caso encerrado). Mesmas condições que zeram o chip de
 * prazo zeram o botão.
 */
export const podeResponderProcesso = (
  processo: CitacaoArbitralListItem,
  referenciaIso?: string,
): boolean =>
  !isProcessoEncerrado(processo) &&
  !processo.jaSubmeteu &&
  (!processo.prazoDataLimite || diasAtePrazo(processo.prazoDataLimite, referenciaIso) > 0);

// Mesmo conjunto de regras do card da lista e do CTA do detalhe: acesso direto à rota de
// responder (link antigo, bookmark) com prazo vencido ou resposta já enviada também bloqueia.
//
// `esclarecimento?.podePedir` é a SEGUNDA janela que a mesma rota aceita (ClickUp 86ak8m7du,
// pós-sentença) — sem este bypass, `canRespond` (sempre `false` depois da sentença: o prazo de
// CONTESTAÇÃO já encerrou há muito) bloquearia toda entrada na tela, mesmo com o prazo de
// Pedido de Esclarecimento aberto.
export const bloqueiaResposta = (detail: CitacaoArbitralDetail | null): boolean =>
  detail !== null &&
  (!podeResponderProcesso(detail) || !detail.canRespond) &&
  !detail.esclarecimento?.podePedir;

export const chipProcesso = (
  processo: CitacaoArbitralListItem,
  referenciaIso?: string,
): ChipProcesso => {
  if (isProcessoEncerrado(processo)) {
    return { rotulo: 'Caso encerrado', tom: 'success', icone: 'check' };
  }

  if (processo.jaSubmeteu) {
    return { rotulo: 'Resposta enviada', tom: 'brand', icone: 'check' };
  }

  if (!processo.prazoDataLimite) {
    return { rotulo: 'Em análise pela Arbitralis', tom: 'neutral', icone: 'balanca' };
  }

  const dias = diasAtePrazo(processo.prazoDataLimite, referenciaIso);

  if (dias <= 0) {
    return { rotulo: 'Prazo encerrado', tom: 'error', icone: 'relogio' };
  }

  return {
    rotulo: dias === 1 ? 'Resposta em 1 dia' : `Resposta em ${dias} dias`,
    tom: 'warning',
    icone: 'relogio',
  };
};
